import React, { useCallback, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectNumberOfItemsForMineTab } from 'flow/selectors/TicketsSelectors';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import useAppContext from 'flow/hooks/useAppContext';
import TabsV2, { TabV2Props } from 'flow/components/pbx/v2/misc/TabsV2';
import ScrollablePillTabs from 'flow/components/ui-components/ScrollablePillTabs';

export type TabsType = 'MY_TICKETS' | 'UNASSIGNED' | 'ALL' | 'FILTER';

const TRACK_EVENT_BY_TAB: Record<TabsType, string> = {
    MY_TICKETS: TICKET_STRINGS.CLICK_TAB_MY_TICKETS,
    UNASSIGNED: TICKET_STRINGS.CLICK_TAB_UNASSIGNED,
    ALL: TICKET_STRINGS.CLICK_TAB_ALL,
    FILTER: TICKET_STRINGS.CLICK_TAB_FILTER,
};

interface Props {
    onClick: (key: TabsType) => void;
    context: string;
    selectedTab?: TabsType;
    /**
     * Show the temporary "Filter" pill. Driven by the parent once the user
     * applies an advanced filter; hidden again when filters are reset.
     */
    showFilterTab?: boolean;
    /** The id of the list the tabs switch; see `TabsV2`. */
    panelId?: string;
}

const TicketsTabs = ({
    context,
    onClick,
    selectedTab,
    showFilterTab = false,
    panelId,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const countForMineTab = useAppSelector(selectNumberOfItemsForMineTab);

    const handleClick = useCallback(
        (tab: TabV2Props) => {
            const key = tab.key as TabsType;
            onClick(key);
            dispatch(
                segmentTrack(TRACK_EVENT_BY_TAB[key], { component: context }),
            );
        },
        [onClick, dispatch, context],
    );

    const tabs: TabV2Props[] = [
        {
            key: 'MY_TICKETS',
            label:
                countForMineTab > 0
                    ? `${i18next.t('tickets.mine')} (${countForMineTab})`
                    : i18next.t('tickets.mine'),
        },
        { key: 'UNASSIGNED', label: i18next.t('tickets.unassigned') },
        { key: 'ALL', label: i18next.t('tickets.all_tickets') },
    ];

    return (
        <ScrollablePillTabs
            tabs={tabs}
            selectedKey={selectedTab}
            onClick={handleClick}
            stretch={false}
            panelId={panelId}
            trailing={
                // Temporary "Filter" pill — rendered outside the tabs array so
                // it can fade + scale in/out. Transition keeps it mounted
                // through the exit animation (200ms ease-out, 80% → 100%).
                <Transition
                    as={Fragment}
                    show={showFilterTab}
                    appear={true}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 scale-[0.8]"
                    enterTo="opacity-100 scale-100"
                    leave="transition ease-out duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-[0.8]"
                >
                    <div>
                        <TabsV2
                            tabs={[
                                {
                                    key: 'FILTER',
                                    label: i18next.t(
                                        'tickets.filter.tab',
                                        'Filter',
                                    ),
                                },
                            ]}
                            selectedKey={selectedTab}
                            onClick={handleClick}
                            panelId={panelId}
                        />
                    </div>
                </Transition>
            }
        />
    );
};

export default React.memo(TicketsTabs);
